import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import { useParams } from 'react-router';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  CircularProgress,
  Paper,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { WorkspacesService } from '../../services/WorkspacesService';
import { TasksDialog } from './TasksDialog';
import { Environment, Provision, PageFilter } from './types';
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';

// Constants
const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 100,
  INITIAL_PAGE: 1,
} as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'RUNNING', label: 'RUNNING' },
  { value: 'SUCCEEDED', label: 'SUCCEEDED' },
  { value: 'FAILED', label: 'FAILED' },
] as const;

// Types
interface FilterState {
  status: string;
  systemId: string;
}

interface PaginationState {
  currentPage: number;
  hasMoreData: boolean;
  isLoadingMore: boolean;
}

const useStyles = makeStyles(theme => ({
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  searchField: {
    minWidth: 300,
  },
  table: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  statusPending: {
    backgroundColor: '#C97937',
    color: '#fff',
    fontWeight: 'bold',
  },
  statusRunning: {
    backgroundColor: '#4575CD',
    color: '#fff',
    fontWeight: 'bold',
  },
  statusSucceeded: {
    backgroundColor: '#62AE6C',
    color: '#fff',
    fontWeight: 'bold',
  },
  statusFailed: {
    backgroundColor: '#BA564D',
    color: '#fff',
    fontWeight: 'bold',
  },
  tableRow: {
    backgroundColor: '#ededed',
  },
  loadMoreButton: {
    backgroundColor: '#f5f5f5',
    borderColor: '#ddd',
    color: '#666',
    '&:hover': {
      backgroundColor: '#eeeeee',
    },
  },
  idRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: '1.1rem',
    overflow: 'visible',
  },
  openTasksButton: {
    fontSize: '0.65rem',
    lineHeight: 1,
    height: 16,
    padding: '0 6px',
    minWidth: 'auto',
    minHeight: 0,
    textTransform: 'none',
  },
}));

export const WorkspaceHistory = () => {
  const classes = useStyles();
  const [filters, setFilters] = useState<FilterState>({ status: '', systemId: '' });
  const [retryTrigger, setRetryTrigger] = useState(0);

  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: PAGINATION_CONFIG.INITIAL_PAGE,
    hasMoreData: true,
    isLoadingMore: false,
  });
  const [provisions, setProvisions] = useState<Provision[]>([]);
  const [selectedProvisionId, setSelectedProvisionId] = useState<string | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { environment, workspaceId } = useParams<{ environment: Environment; workspaceId: string }>();

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi],
  );

  const handleOpenTasks = useCallback((provisionId: string) => {
    setSelectedProvisionId(provisionId);
    setTasksDialogOpen(true);
  }, []);

  const handleCloseTasksDialog = useCallback(() => {
    setTasksDialogOpen(false);
  }, []);

  // Load catalog
  const { value: catalogSystems } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'System',
        'metadata.annotations.open.totvs.io/app-code': CATALOG_FILTER_EXISTS,
      },
    });
    return response.items;
  }, []);

  // Helper function to get system title from catalog
  const getSystemTitle = useCallback((systemId: string): string => {
    if (!catalogSystems) return '';

    const catalogEntity = catalogSystems.find(entity =>
      entity.metadata.annotations?.['open.totvs.io/app-code'] === systemId
    );

    return catalogEntity?.metadata.title || '';
  }, [catalogSystems]);

  // Helper function to create page request
  const createPageRequest = useCallback((page: number): PageFilter => ({
    pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
    page,
  }), []);

  // Load initial data
  const { loading, error } = useAsync(async () => {
    if (!environment || !workspaceId) return;

    try {
      const workspacesService = new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi);
      const pageRequest = createPageRequest(PAGINATION_CONFIG.INITIAL_PAGE);

      const result = await workspacesService.getProvisions(environment, workspaceId, pageRequest);
      setProvisions(result?.items || []);
      setPagination(prev => ({
        ...prev,
        currentPage: PAGINATION_CONFIG.INITIAL_PAGE,
        hasMoreData: result.hasNext,
      }));
    } catch (err) {
      console.error('Error loading provisions:', err);
      throw err;
    }
  }, [environment, workspaceId, retryTrigger, createPageRequest]);

  // Silent refresh polling para provisions
  useEffect(() => {
    if (!environment || !workspaceId || loading || error) return;
    const intervalId = setInterval(() => {
      setRetryTrigger(prev => prev + 1);
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [environment, workspaceId, loading, error]);

  const handleRetry = useCallback(() => {
    setPagination({
      currentPage: PAGINATION_CONFIG.INITIAL_PAGE,
      hasMoreData: true,
      isLoadingMore: false,
    });
    setProvisions([]);
    setRetryTrigger(prev => prev + 1);
  }, []);

  const loadMoreData = useCallback(async () => {
    if (!environment || !workspaceId || pagination.isLoadingMore || !pagination.hasMoreData) {
      return;
    }

    setPagination(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const workspacesService = new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi);
      const nextPage = pagination.currentPage + 1;
      const pageRequest = createPageRequest(nextPage);

      const result = await workspacesService.getProvisions(environment, workspaceId, pageRequest);

      setProvisions(prev => [...(prev || []), ...(result?.items || [])]);
      setPagination(prev => ({
        ...prev,
        currentPage: nextPage,
        hasMoreData: result.hasNext,
        isLoadingMore: false,
      }));
    } catch (err) {
      console.error('Error loading more provisions:', err);
      setPagination(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [environment, workspaceId, pagination, fetchApi, microsoftAuthApi, discoveryApi, catalogApi, createPageRequest]);

  const handleFilterChange = useCallback((key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const filteredProvisions = useMemo(() => {
    if (!provisions || provisions.length === 0) return [];

    return provisions.filter((provision) => {
      const statusMatch = !filters.status || provision.status === filters.status;
      const systemIdMatch = !filters.systemId ||
        provision.systemIds
          .trim().split(',')
          .some(systemId => 
            systemId.toLowerCase().includes(filters.systemId.toLowerCase())
            || getSystemTitle(systemId).toLowerCase().includes(filters.systemId.toLowerCase())
          );

      return statusMatch && systemIdMatch;
    });
  }, [provisions, filters]);

  const getStatusChipClass = (status: string) => {
    switch (status) {
      case 'PENDING':
        return classes.statusPending;
      case 'RUNNING':
        return classes.statusRunning;
      case 'SUCCEEDED':
        return classes.statusSucceeded;
      case 'FAILED':
        return classes.statusFailed;
      default:
        return '';
    }
  };

  return (
    <Card>
      <CardHeader title="Activity History" />
      <CardContent>
        {/* Filters Section */}
        <Box className={classes.filterContainer}>
          <FormControl
            className={classes.formControl}
            variant="outlined"
            size="small"
          >
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value as string)}
              label="Status"
            >
              {STATUS_OPTIONS?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <em>{option.label}</em>
                </MenuItem>
              )) || []}
            </Select>
          </FormControl>

          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="System"
            value={filters.systemId}
            onChange={e => handleFilterChange('systemId', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Table size="small" className={classes.table}>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" style={{ marginLeft: 16 }}>
                      Loading history...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Paper style={{ padding: 16, backgroundColor: '#ffebee' }}>
                    <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                      Error loading history: {error.message}
                    </Typography>
                    <Button
                      color="primary"
                      size="small"
                      variant="outlined"
                      onClick={handleRetry}
                    >
                      Retry
                    </Button>
                  </Paper>
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && (filteredProvisions || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No history items found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && (filteredProvisions || []).length > 0 &&
              (filteredProvisions || []).map((provision: Provision) => (
                <TableRow key={provision.id} className={classes.tableRow}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {provision.systemIds
                          ?.trim().split(',')
                          .map(systemId => `${getSystemTitle(systemId)} (${systemId})`)
                          .join(', ') || 'No systems'
                        }
                      </Typography>
                      <Box className={classes.idRow}>
                        <Typography variant="caption" color="textSecondary">
                          ID: {provision.id || 'Unknown'}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          className={classes.openTasksButton}
                          onClick={() => handleOpenTasks(provision.id)}
                        >
                          Open tasks
                        </Button>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Origin: {provision.origin || 'Unknown'} | Created by: {provision.createdBy || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {provision.createdAt ? new Date(provision.createdAt).toLocaleString() : 'Unknown date'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={provision.status || 'Unknown'}
                      className={getStatusChipClass(provision.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>

        {/* Load More Button */}
        {pagination.hasMoreData && !loading && !error && (
          <Box mt={2}>
            <Button
              variant="outlined"
              color="default"
              fullWidth
              onClick={loadMoreData}
              disabled={pagination.isLoadingMore}
              className={classes.loadMoreButton}
            >
              {pagination.isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}

        {environment && (
          <TasksDialog
            open={tasksDialogOpen}
            onClose={handleCloseTasksDialog}
            provisionId={selectedProvisionId}
            environment={environment}
            workspacesService={workspacesService}
          />
        )}
      </CardContent>
    </Card>
  );
};